import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Factory, Building, Store, Hotel, Camera, KeyRound, Volume2,
  Cable, PhoneCall, ChevronRight, ChevronLeft, CheckCircle2, MessageCircle, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const facilities = [
  { id: "Commercial Tower / Office", icon: Building2 },
  { id: "Factory / Warehouse", icon: Factory },
  { id: "Residential High-rise", icon: Building },
  { id: "Retail Store / F&B", icon: Store },
  { id: "Hotel / Hospitality", icon: Hotel },
];

const services = [
  { id: "CCTV Surveillance", icon: Camera, base: 3500, desc: "IP cameras, NVR, AI analytics" },
  { id: "Access Control & Alarm", icon: KeyRound, base: 4500, desc: "Biometric, RFID, intrusion alarm" },
  { id: "PA / AV Systems", icon: Volume2, base: 5000, desc: "Public address, BGM, conference AV" },
  { id: "ELV Cabling & Network", icon: Cable, base: 4000, desc: "Structured cabling, fiber, racks" },
  { id: "Intercom & Gate Systems", icon: PhoneCall, base: 3000, desc: "Video intercom, barriers, turnstiles" },
];

const scales = [
  { id: "Small", desc: "Single unit / shoplot", mult: 1 },
  { id: "Medium", desc: "Multi-room / 1–2 floors", mult: 1.8 },
  { id: "Large", desc: "Multi-floor / large facility", mult: 3.2 },
  { id: "Enterprise", desc: "Whole building / multi-site", mult: 5.5 },
];

const fmtRM = (n) => `RM ${Math.round(n / 100) * 100 >= 1000 ? (Math.round(n / 100) * 100).toLocaleString("en-MY") : Math.round(n / 100) * 100}`;

export default function Quote() {
  const [step, setStep] = useState(0);
  const [facility, setFacility] = useState("");
  const [selected, setSelected] = useState([]);
  const [scale, setScale] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const toggleService = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const estimate = (() => {
    if (!selected.length || !scale) return null;
    const base = services.filter((s) => selected.includes(s.id)).reduce((a, s) => a + s.base, 0);
    const low = base * scale.mult;
    return { low, high: low * 1.6 };
  })();

  const canNext =
    (step === 0 && facility) ||
    (step === 1 && selected.length > 0) ||
    (step === 2 && scale) ||
    step === 3;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const details = [
      form.message,
      `Premise scale: ${scale?.id} (${scale?.desc})`,
      estimate ? `Indicative budget range shown to customer: ${fmtRM(estimate.low)} - ${fmtRM(estimate.high)}` : "",
    ].filter(Boolean).join("\n");
    try {
      const res = await axios.post(`${API}/quote`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        service: selected.join(", "),
        facility_type: facility,
        message: details,
      });
      setResult(res.data);
      toast.success("Quote request submitted successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = ["Facility", "Services", "Scale", "Details"];

  if (result) {
    return (
      <div className="pt-32 pb-24 min-h-screen" data-testid="quote-page">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glow-card p-10">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-5" />
            <h1 className="font-display text-3xl font-bold text-white">Request Received</h1>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Thank you, {result.ref ? <span className="text-cyan-400 font-tech">{result.ref}</span> : ""} — our team
              will review your requirements and contact you within 1 business day.
            </p>
            {!result.email_sent && (
              <p className="mt-3 text-sm text-amber-400">Email notification is delayed, but your request is saved — we will still respond.</p>
            )}
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={`https://wa.me/601172283321?text=Hello%20YC%20SYSTEM%2C%20I%20just%20submitted%20quote%20request%20${result.ref}.`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-success-whatsapp"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#25D366]/60 text-white font-semibold hover:bg-[#25D366]/10 transition-colors"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366]" /> Follow Up on WhatsApp
              </a>
              <button
                onClick={() => { setResult(null); setStep(0); setFacility(""); setSelected([]); setScale(null); setForm({ name: "", email: "", phone: "", company: "", message: "" }); }}
                data-testid="quote-new-request-button"
                className="px-6 py-3 border border-white/20 text-slate-300 hover:text-white font-semibold transition-colors"
              >
                New Request
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="quote-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="tech-label">Request a Quote</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white mt-3">
            Build Your System Quote
          </h1>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Answer 4 quick steps and get an indicative scope instantly. Our engineers follow up with a detailed proposal.
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-10">
          {stepLabels.map((l, i) => (
            <div key={l} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 flex items-center justify-center font-tech text-sm border transition-colors ${
                    i <= step ? "bg-blue-600 text-white border-cyan-400/40" : "text-slate-500 border-white/15"
                  }`}
                  data-testid={`stepper-${i}`}
                >
                  {i + 1}
                </div>
                <span className={`text-xs mt-2 ${i <= step ? "text-cyan-400" : "text-slate-500"}`}>{l}</span>
              </div>
              {i < stepLabels.length - 1 && <div className={`h-px flex-1 mx-2 mb-6 ${i < step ? "bg-cyan-400/50" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            {step === 0 && (
              <div data-testid="system-calculator-step-1">
                <h2 className="font-display text-xl font-semibold text-white mb-5">What type of facility?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {facilities.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setFacility(f.id)}
                      data-testid={`facility-${f.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`glow-card p-5 flex items-center gap-4 text-left ${facility === f.id ? "!border-cyan-400/70 bg-blue-600/10" : ""}`}
                    >
                      <f.icon className={`w-6 h-6 ${facility === f.id ? "text-cyan-400" : "text-slate-400"}`} />
                      <span className="font-semibold text-white text-sm">{f.id}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="system-calculator-step-2">
                <h2 className="font-display text-xl font-semibold text-white mb-5">Which systems do you need?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => toggleService(s.id)}
                      data-testid={`service-${s.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`glow-card p-5 text-left ${selected.includes(s.id) ? "!border-cyan-400/70 bg-blue-600/10" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <s.icon className={`w-6 h-6 ${selected.includes(s.id) ? "text-cyan-400" : "text-slate-400"}`} />
                        {selected.includes(s.id) && <CheckCircle2 className="w-5 h-5 text-cyan-400" />}
                      </div>
                      <div className="font-semibold text-white text-sm mt-3">{s.id}</div>
                      <div className="text-xs text-slate-400 mt-1">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="system-calculator-step-3">
                <h2 className="font-display text-xl font-semibold text-white mb-5">How large is the premises?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {scales.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setScale(s)}
                      data-testid={`scale-${s.id.toLowerCase()}`}
                      className={`glow-card p-5 text-left ${scale?.id === s.id ? "!border-cyan-400/70 bg-blue-600/10" : ""}`}
                    >
                      <div className="font-semibold text-white">{s.id}</div>
                      <div className="text-xs text-slate-400 mt-1">{s.desc}</div>
                    </button>
                  ))}
                </div>
                {estimate && (
                  <div className="mt-6 glow-card p-5 border-cyan-400/30" data-testid="estimate-preview">
                    <span className="tech-label">Indicative Budget Range</span>
                    <div className="font-display text-2xl font-bold text-white mt-2">
                      {fmtRM(estimate.low)} – {fmtRM(estimate.high)}
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Preliminary estimate only. Final pricing depends on site survey, brands and quantities.
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <form onSubmit={submit} data-testid="quote-form">
                <h2 className="font-display text-xl font-semibold text-white mb-5">Your contact details</h2>
                <div className="glow-card p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="tech-label !text-[0.6rem] block mb-2">Full Name *</label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="quote-form-name-input"
                        className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                        placeholder="Ahmad / Jane Tan"
                      />
                    </div>
                    <div>
                      <label className="tech-label !text-[0.6rem] block mb-2">Company</label>
                      <input
                        value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                        data-testid="quote-form-company-input"
                        className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                        placeholder="Company Sdn Bhd"
                      />
                    </div>
                    <div>
                      <label className="tech-label !text-[0.6rem] block mb-2">Email *</label>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="quote-form-email-input"
                        className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                        placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <label className="tech-label !text-[0.6rem] block mb-2">Phone / WhatsApp *</label>
                      <input
                        required
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="quote-form-phone-input"
                        className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors"
                        placeholder="01x-xxxx xxxx"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="tech-label !text-[0.6rem] block mb-2">Selected Services</label>
                    <input
                      readOnly
                      value={selected.join(", ")}
                      data-testid="quote-form-service-select"
                      className="w-full bg-slate-900/60 border border-white/10 px-4 py-3 text-cyan-400 text-sm font-tech"
                    />
                  </div>
                  <div>
                    <label className="tech-label !text-[0.6rem] block mb-2">Project Details *</label>
                    <textarea
                      required
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="quote-form-message-input"
                      className="w-full bg-slate-900/80 border border-white/15 px-4 py-3 text-white text-sm focus:border-cyan-400 focus:outline-none transition-colors resize-none"
                      placeholder="e.g. 16 cameras for a 2-storey warehouse in Shah Alam, plus door access for 4 doors…"
                    />
                  </div>
                  {estimate && (
                    <div className="border border-cyan-400/30 bg-cyan-400/5 p-4 text-sm">
                      <span className="text-slate-300">Indicative range: </span>
                      <span className="font-tech text-cyan-400">{fmtRM(estimate.low)} – {fmtRM(estimate.high)}</span>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-form-submit-button"
                  className="mt-6 w-full px-8 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold transition-colors border border-cyan-400/40 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit Quote Request <ChevronRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex justify-between">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="wizard-back-button"
            className="px-6 py-3 border border-white/15 text-slate-300 hover:text-white disabled:opacity-40 font-semibold text-sm transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 && (
            <button
              onClick={() => canNext && setStep((s) => s + 1)}
              disabled={!canNext}
              data-testid={step === 2 ? "system-calculator-submit" : "wizard-next-button"}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white font-semibold text-sm transition-colors border border-cyan-400/40 flex items-center gap-2"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
